import { useEffect, useMemo, useState } from 'react';
import { Select } from '../../components/ui';
import type { HubProjectRow } from '../hub/api';

/**
 * THE hub-project picker: organization first, then project. One control for
 * one idea — the link modal on an agreement page and the New-agreement modal
 * both render this, so the two doors cannot drift apart (house rule: a second
 * copy is how two screens start disagreeing).
 *
 * ⚠ LINKED ROWS ARE OFFERED TOO. Several agreements draw down ONE hub project
 * in sequence — that is the rollover model — so "already linked" is an
 * annotation, not an exclusion. Only archived-in-hub rows are withheld.
 */
export function HubProjectPicker({
  rows,
  value,
  onChange,
  agreementId,
  clientId,
}: {
  rows: HubProjectRow[];
  /** The chosen hub project id ('' = none yet). */
  value: string;
  onChange: (hubProjectId: string) => void;
  /** When linking an EXISTING agreement: lets the suggested match lead. */
  agreementId?: string;
  /**
   * When creating a NEW agreement for a chosen client: that client's hub
   * organization is the starting point. Jesse, 2026-09-10, on a fresh client
   * whose hub project sat two dropdowns away: "nothing shows up". The rows
   * already carry `client` (matched by hubOrgId), so no second fetch.
   */
  clientId?: string;
}) {
  const [orgChoice, setOrgChoice] = useState('');
  // A new client means a new starting organization; a pick made for the old
  // client would point the list at somebody else's projects.
  useEffect(() => {
    setOrgChoice('');
  }, [clientId]);

  const linkable = useMemo(() => rows.filter((r) => !r.archivedInHub), [rows]);

  const suggestedRow = useMemo(
    () =>
      agreementId
        ? (linkable.find((r) => r.suggestedMatch?.agreementId === agreementId) ?? null)
        : null,
    [linkable, agreementId],
  );

  /** One row per organization, alphabetical; the agency's own projects group
   *  under their real org name like everyone else. */
  const orgOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const r of linkable) byId.set(r.clientOrgId, r.clientOrgName ?? r.clientOrgId);
    return [...byId.entries()]
      .map(([v, label]) => ({ value: v, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [linkable]);

  const clientOrg = useMemo(
    () => (clientId ? (linkable.find((r) => r.client?.id === clientId)?.clientOrgId ?? '') : ''),
    [linkable, clientId],
  );

  // An explicit pick wins; else the suggested match's org; else the chosen client's org.
  const effectiveOrg = orgChoice || suggestedRow?.clientOrgId || clientOrg || '';

  const options = useMemo(
    () =>
      linkable
        .filter((r) => r.clientOrgId === effectiveOrg)
        .sort((a, b) => {
          const aMine = a.suggestedMatch?.agreementId === agreementId ? 0 : 1;
          const bMine = b.suggestedMatch?.agreementId === agreementId ? 0 : 1;
          return aMine - bMine || a.name.localeCompare(b.name);
        })
        .map((r) => ({
          value: r.hubProjectId,
          label:
            agreementId && r.suggestedMatch?.agreementId === agreementId
              ? `${r.name} — suggested match`
              : r.linked
                ? `${r.name} · ${
                    r.agreements.length === 1
                      ? `linked to “${r.agreements[0].name}”`
                      : `${r.agreements.length} agreements linked`
                  }`
                : r.name,
        })),
    [linkable, effectiveOrg, agreementId],
  );

  return (
    <>
      <Select
        label="Organization"
        placeholder={orgOptions.length ? 'Choose an organization…' : 'No hub projects to link'}
        value={effectiveOrg}
        onChange={(e) => {
          setOrgChoice(e.target.value);
          // A project belongs to ONE org; carrying a pick across orgs would
          // link something the reader is no longer looking at.
          onChange('');
        }}
        options={orgOptions}
        disabled={orgOptions.length === 0}
      />
      <Select
        label="Hub project"
        placeholder={
          !effectiveOrg
            ? 'Pick an organization first'
            : options.length
              ? 'Choose a hub project…'
              : 'No live hub projects in this organization'
        }
        value={value}
        onChange={(e) => onChange(e.target.value)}
        options={options}
        disabled={!effectiveOrg || options.length === 0}
      />
    </>
  );
}
